// FILE: src/pages/info/AccessibilityPage.jsx
// Accessibility statement (CD5.2: AODA / IASR, WCAG 2.0 AA): what the site
// does, known limits, accessible formats on request (IASR s.12), feedback
// (s.11) and notice of planned interruptions.
import React from "react";
import { Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InfoPage from "./InfoPage";
import { Section } from "../../components/ui";

const FEATURES = [
  "Works with screen readers, keyboard-only use and voice control",
  "Text at least 12 points, resizable to 200 % with browser zoom, and an in-app text size setting",
  "Light and dark themes, both meeting WCAG 2.0 AA contrast",
  "Links are underlined and status is never shown by colour alone",
  "Lesson videos have closed captions, with pause, replay and volume controls",
  "A warning before you're signed out for inactivity, with one click to stay signed in",
  "A glossary of course terms and abbreviations on every page"
];

export default function AccessibilityPage({ variant = "public" }) {
  const contact = variant === "student" ? "/student/support/new" : "/accommodation";
  return (
    <InfoPage
      variant={variant}
      title="Accessibility"
      description="Our commitment to an accessible course, under Ontario's Accessibility for Ontarians with Disabilities Act (AODA)."
    >
      <Stack spacing={3} sx={{ maxWidth: 860 }}>
        <Section id="commitment" title="Our commitment">
          <Typography>
            We want every student to be able to take this course independently. The site is designed and tested to meet
            the Web Content Accessibility Guidelines (WCAG) 2.0 level AA, as Ontario's Integrated Accessibility
            Standards Regulation requires.
          </Typography>
        </Section>
        <Section id="features" title="What the site does">
          <Stack component="ul" spacing={1} sx={{ pl: 3, my: 0 }}>
            {FEATURES.map((f) => (
              <Typography component="li" key={f}>
                {f}
              </Typography>
            ))}
          </Stack>
        </Section>
        <Section id="formats" title="Accessible formats and accommodations">
          <Typography>
            You can ask for any course document (for example the syllabus, the Code of Conduct or your course record) in
            another format, such as large print, plain text or an accessible PDF, and for accommodations such as extra
            support with sign-in steps. There's no cost.{" "}
            <Link component={RouterLink} to={contact}>
              {variant === "student" ? "Ask student service support" : "Request an accommodation"}
            </Link>
            .
          </Typography>
        </Section>
        <Section id="feedback" title="Feedback and planned interruptions">
          <Typography>
            Tell us about any barrier you find, in any way that works for you: through support, by phone or by email.
            We'll reply within 48 hours. Planned maintenance that interrupts the course is announced to students in
            advance.
          </Typography>
        </Section>
      </Stack>
    </InfoPage>
  );
}
